import { jsx as _jsx } from "react/jsx-runtime";
import { AxiosError } from 'axios';
import { createContext, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { I18nUtils } from '@medable/i18n-core';
import { isPseudoLocale } from 'libs.i18n-core';
import { LocaleContext, SetLocaleContext, SetUserTimeZoneContext, Spinner, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { usePersistentValue, } from 'libs.react';
import { ApiClientService, useApiClient } from '../../api_client';
import { AuthEndpoint, AuthRoute, } from '../types';
const isMedableIdentity = (identity) => {
    return identity.email.endsWith('@medable.com');
};
const missingAuthProvider = () => {
    throw Error('Missing the auth provider');
};
export const AuthContext = createContext({
    entity: undefined,
    entityId: 0,
    forgotPassword: missingAuthProvider,
    getIDPConfiguration: missingAuthProvider,
    isInternalUser: false,
    isLoading: true,
    isLoggedIn: false,
    lastEntities: [],
    login: missingAuthProvider,
    logout: missingAuthProvider,
    resetExpiredSession: missingAuthProvider,
    mfaChallenge: undefined,
    sendMfaChallenge: missingAuthProvider,
    verifyMfaChallenge: missingAuthProvider,
    enrollMfa: missingAuthProvider,
    verifyMfaEnrollment: missingAuthProvider,
    clearMfaChallenge: missingAuthProvider,
    navigateToLogin: missingAuthProvider,
    redirectToDefaultPage: missingAuthProvider,
    refreshEntity: missingAuthProvider,
    refreshIdentity: missingAuthProvider,
    resetPassword: missingAuthProvider,
    scopes: {},
    setEntity: missingAuthProvider,
    ssoLogin: missingAuthProvider,
    user: undefined,
    roleGroups: [],
    userEntities: [],
});
const isSessionExpiredError = (err) => {
    return err instanceof AxiosError && err.response?.status === 401;
};
const isValidMedableRedirectUrl = (url) => {
    const allowedDomains = ['medable.com', 'medable.cn'];
    try {
        const { hostname } = new URL(url);
        return allowedDomains.some((domain) => hostname.endsWith(`.${domain}`));
    }
    catch {
        return false;
    }
};
// IAM returns customDomain as a full origin (https://host); MoM stores it as a bare hostname.
// Normalize to a full origin so window.location.href treats it as absolute, not a relative path.
// Gate on the same allow-list used for redirect params — customDomain is server-supplied and
// drives a full-page navigation with session cookies attached.
const normalizeCustomDomainOrigin = (customDomain) => {
    const candidate = /^https?:\/\//i.test(customDomain) ? customDomain : `https://${customDomain}`;
    try {
        const { origin } = new URL(candidate);
        return isValidMedableRedirectUrl(origin) ? origin : undefined;
    }
    catch {
        return undefined;
    }
};
const iamServiceEndPoint = process.env.IAM_SERVICE_ENDPOINT;
const ENTITY_INFO_KEY = 'AUTH_LOGIN_ENTITIES';
/**
 * The AuthProvider is the main context provider to handle the user information and authentication
 * It communicates with the IAM service to validate the user credentials and store the token
 */
export const AuthProvider = ({ children }) => {
    const iamClient = useApiClient(ApiClientService.IAM);
    const momClient = useApiClient(ApiClientService.MOM);
    const { addNotification } = useToastNotification();
    const translate = useLocalizeMessage();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [lastEntities, setLastEntities] = usePersistentValue(ENTITY_INFO_KEY, []);
    const [user, setUser] = useState();
    const [scopes, setScopes] = useState({});
    const [roleGroups, setRoleGroups] = useState([]);
    const [isInternalUser, setIsInternalUser] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [entityId, setEntityId] = useState(0);
    const [entity, setEntity] = useState();
    const [userEntities, setUserEntities] = useState([]);
    const setUserTimeZone = useContext(SetUserTimeZoneContext);
    const setLocale = useContext(SetLocaleContext);
    const currentLocale = useContext(LocaleContext);
    const [mfaChallenge, setMfaChallenge] = useState();
    const entityIdRef = useRef(entityId);
    entityIdRef.current = entityId;
    const currentLocaleRef = useRef(currentLocale);
    currentLocaleRef.current = currentLocale;
    const entityLoadSequenceRef = useRef(0);
    const pendingTargetEntityIdRef = useRef();
    const isResettingExpiredSessionRef = useRef(false);
    const searchParamsRef = useRef(searchParams);
    const pathnameRef = useRef(pathname);
    searchParamsRef.current = searchParams;
    pathnameRef.current = pathname;
    /**
     * Seed the app locale from the stored identity locale so the user's saved preference survives a
     * page refresh. Mirrors the time-zone seeding below. The identity stores the locale in cortex
     * format (e.g. `es_US`) while LocaleContext expects i18n format (e.g. `es-US`). This is a no-op
     * in apps that mount their own I18nContextProvider below AuthProvider (e.g. site_app), where
     * SetLocaleContext resolves to null.
     *
     * A pseudo-locale (`en-XA`, `ar-XB`) is an in-session localization-testing choice that is never
     * persisted to the identity, so an active pseudo-locale takes precedence and is left untouched —
     * otherwise the refreshIdentity that runs right after a profile save would clobber it. The ref
     * keeps this correct when called from async callbacks that would capture a stale locale.
     */
    const applyIdentityLocale = (locale) => {
        if (!locale || !setLocale || isPseudoLocale(currentLocaleRef.current)) {
            return;
        }
        setLocale(I18nUtils.cortexToI18nFormat(locale));
    };
    /**
     * Shared helper to complete login from a standard IAM login response
     * Used after normal login, MFA challenge verify, and MFA enrollment verify
     */
    const completeLoginFromResponse = async (data) => {
        const targetEntityId = mfaChallenge?.entityId || data.loginEntity;
        pendingTargetEntityIdRef.current = targetEntityId;
        const targetEntity = await getEntities(targetEntityId);
        const targetCustomDomain = targetEntity?.configuration?.customDomain || data.customDomain;
        saveLastEntity(targetEntityId, data.identity.email);
        verifyCustomDomain(targetCustomDomain || undefined);
        setIsInternalUser(isMedableIdentity(data.identity));
        setIsLoggedIn(true);
        setUser(data.identity);
        setScopes(data.scopes);
        setRoleGroups(data.roleGroups);
        setMfaChallenge(undefined);
        if (data.identity.tz) {
            if (setUserTimeZone) {
                setUserTimeZone(data.identity.tz);
            }
            else {
                console.warn('AuthProvider requires I18nContextProvider to set user time zone.');
            }
        }
        applyIdentityLocale(data.identity.locale);
    };
    useEffect(() => {
        refreshIdentity();
    }, []);
    useEffect(() => {
        if (isLoading) {
            return;
        }
        const errorDescription = searchParams.get('error_description');
        if (errorDescription) {
            addNotification({
                title: translate('Error'),
                subtitle: errorDescription,
                type: 'error',
            });
            searchParams.delete('error_description');
            setSearchParams(searchParams);
        }
        if (searchParams.get('expired-session') === 'true' && !isResettingExpiredSessionRef.current) {
            isResettingExpiredSessionRef.current = true;
            void (async () => {
                try {
                    await resetExpiredSession();
                    searchParams.delete('expired-session');
                    setSearchParams(searchParams);
                }
                finally {
                    isResettingExpiredSessionRef.current = false;
                }
            })();
            return;
        }
    }, [searchParams, isLoading]);
    const refreshIdentity = async () => {
        setIsLoading(true);
        try {
            const { data } = await iamClient.get(AuthEndpoint.ME);
            await getEntities(data.loginEntity);
            saveLastEntity(data.loginEntity, data.identity.email);
            verifyCustomDomain(data.customDomain);
            if (data && data.identity) {
                setIsInternalUser(isMedableIdentity(data.identity));
                setIsLoggedIn(true);
                setUser(data.identity);
                setRoleGroups(data.roleGroups);
                setScopes(data.scopes);
                if (data.identity.tz) {
                    if (setUserTimeZone) {
                        setUserTimeZone(data.identity.tz);
                    }
                    else {
                        console.warn('AuthProvider requires I18nContextProvider to set user time zone.');
                    }
                }
                applyIdentityLocale(data.identity.locale);
            }
            else {
                cleanUserInformation();
            }
        }
        catch {
            cleanUserInformation();
        }
        finally {
            setIsLoading(false);
            setIsInitialized(true);
        }
    };
    const refreshEntity = async (id = entityId) => {
        const requestSequence = ++entityLoadSequenceRef.current;
        try {
            const { data: response } = await momClient.get(`/v1/entities/${id}`);
            if (requestSequence !== entityLoadSequenceRef.current) {
                return;
            }
            setEntity(response.data);
            setEntityId(response.data.id);
        }
        catch (error) {
            if (requestSequence !== entityLoadSequenceRef.current) {
                return;
            }
            console.error('Error getting entities: ', error);
            setEntity(undefined);
            setEntityId(0);
        }
    };
    const getEntities = async (newEntityId) => {
        const requestSequence = ++entityLoadSequenceRef.current;
        const [entityResult, userEntitiesResult] = await Promise.allSettled([
            momClient.get(`/v1/entities/${newEntityId}`),
            momClient.get('/v1/entities', {
                params: { hasEntityAccess: true },
            }),
        ]);
        if (requestSequence !== entityLoadSequenceRef.current) {
            return undefined;
        }
        let resolvedEntity;
        if (entityResult.status === 'fulfilled') {
            resolvedEntity = entityResult.value.data.data;
            setEntity(resolvedEntity);
            setEntityId(resolvedEntity.id);
        }
        else {
            console.error('Error getting entity: ', entityResult.reason);
            setEntity(undefined);
            setEntityId(0);
        }
        if (userEntitiesResult.status === 'fulfilled') {
            setUserEntities(userEntitiesResult.value.data.data || []);
        }
        else {
            console.error('Error getting entities: ', userEntitiesResult.reason);
            setUserEntities([]);
        }
        return resolvedEntity;
    };
    const saveLastEntity = (newEntityId, email) => {
        try {
            const newLastEntities = lastEntities.filter((entityInfo) => entityInfo.email !== email);
            setLastEntities([...newLastEntities, { entityId: newEntityId, email }]);
        }
        catch (error) {
            console.error('Error saving last entities information', error);
        }
    };
    /**
     * Checks the IDPs associated to the given email and returns those
     */
    const getIDPConfiguration = async (email) => {
        setIsLoading(true);
        try {
            const { data: response } = await iamClient.get(`/v1/sso/check-username`, {
                params: { email, loginMethods: ['all'] },
            });
            setIsLoading(false);
            return response.data.map((entity) => {
                const enabledIdps = entity.idps.filter((idp) => idp.enabled);
                return {
                    ...entity,
                    idps: enabledIdps,
                    loginMethods: enabledIdps.length > 0 ? entity.loginMethods : entity.loginMethods.filter((m) => m !== 'sso'),
                };
            });
        }
        catch (error) {
            console.error('Error getting IDP configuration: ', error);
            setIsLoading(false);
            throw error;
        }
    };
    const cleanUserInformation = () => {
        setUser(undefined);
        setIsInternalUser(false);
        setIsLoggedIn(false);
        setEntity(undefined);
        setUserEntities([]);
        setMfaChallenge(undefined);
        pendingTargetEntityIdRef.current = undefined;
        setEntityId(0);
        setScopes({});
        setRoleGroups([]);
    };
    const getExpiredSessionLoginPath = () => {
        const params = new URLSearchParams();
        params.set('expired-session', 'true');
        const redirect = searchParamsRef.current.get('redirect');
        if (redirect) {
            params.set('redirect', redirect);
        }
        return `${AuthRoute.LOGIN}?${params.toString()}`;
    };
    const resetExpiredSession = async () => {
        try {
            await iamClient.post(AuthEndpoint.LOGOUT, undefined, {
                no400Handling: true,
                no401Handling: true,
                no403Handling: true,
                no404Handling: true,
                no500Handling: true,
            });
        }
        catch {
        }
        finally {
            cleanUserInformation();
        }
    };
    const handleExpiredSessionDuringMfa = async () => {
        await resetExpiredSession();
        navigate(getExpiredSessionLoginPath());
    };
    /**
     * Used to log the user in the application and set the token and user identity information
     */
    const login = async (email, password, entityId) => {
        setIsLoading(true);
        try {
            const lastEntity = lastEntities.find((entityInfo) => entityInfo.email === email);
            const { data } = await iamClient.post(AuthEndpoint.LOGIN, {
                email,
                password,
                entityId: entityId || lastEntity?.entityId,
            });
            if (data.mfaRequired) {
                const resolvedEntityId = entityId || lastEntity?.entityId;
                setMfaChallenge({
                    mfaMethods: data.mfaMethods || [],
                    allowedMfaMethods: data.allowedMfaMethods || [],
                    needsEnrollment: !data.mfaMethods || data.mfaMethods.length === 0,
                    loginEmail: email,
                    mobile: data.identity?.mobile || data.mobile,
                    entityId: resolvedEntityId,
                });
                return { mfaRequired: true };
            }
            await completeLoginFromResponse(data);
            return { mfaRequired: false };
        }
        catch (err) {
            if (err instanceof AxiosError && !err.response?.data.errorHandled) {
                addNotification({
                    title: 'Error trying to login',
                    subtitle: err.response?.data.message || 'There was an error logging in. Please try again',
                    type: 'error',
                });
            }
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * Removes any stored user identity information and calls to invalidate the token on the IAM service
     */
    const logout = async () => {
        await iamClient.post(AuthEndpoint.LOGOUT);
        cleanUserInformation();
    };
    /**
     * Triggers the forgot password flow, that will send an email to the user with a link to reset the password
     */
    const forgotPassword = async (email) => {
        setIsLoading(true);
        try {
            await iamClient.post(AuthEndpoint.FORGOT_PASSWORD, { email });
        }
        catch (err) {
            if (err instanceof AxiosError && !err.response?.data.errorHandled) {
                addNotification({
                    title: 'Error trying to send reset password link',
                    subtitle: err.response?.data.message || 'There was an error trying to send the reset password link. Please try again',
                    type: 'error',
                });
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    /**
     * Triggers the SSO login flow, allowing the user to login using some configured IDP service
     */
    const ssoLogin = (idpConfiguration, email, txn, idpKey) => {
        setIsLoading(true);
        const currentSearchParams = searchParamsRef.current;
        const url = `${iamServiceEndPoint}${AuthEndpoint.SSO_LOGIN}?`;
        const location = currentSearchParams.has('location')
            ? `?location=${currentSearchParams.get('location')}`
            : undefined;
        const redirect = currentSearchParams.get('redirect');
        // When `redirect` is already an absolute URL on a trusted Medable domain — the Agents Studio
        // app on its own subdomain, or a deep link from a sibling Studio app like Sentinel — pass it
        // through to IAM untouched. Otherwise treat it as a path on the current origin. Without this
        // branch an absolute Medable URL would be concatenated after window.location.origin,
        // producing a malformed `https://a.medable.comhttps://b.medable.com/...` that IAM cannot
        // redirect to after SSO completes. Mirrors the allow-list check in `redirectToDefaultPage`.
        const return_to = redirect && isValidMedableRedirectUrl(redirect)
            ? redirect
            : `${window.location.origin}${redirect || location || '/'}`;
        const newSearchParams = new URLSearchParams({
            idp: idpKey || idpConfiguration.key,
            return_to,
            email,
            ...(txn && { txn }),
        });
        document.location.href = `${url}${newSearchParams.toString()}`;
    };
    /**
     * Used to reset the user password, normally used when the user has received an email to reset the password
     * either if it is to reset the password or to set the password by the first time
     */
    const resetPassword = async (password, token) => {
        setIsLoading(true);
        try {
            await iamClient.post(`${AuthEndpoint.RESET_PASSWORD}${token}`, { password });
            addNotification({
                title: translate('Reset password'),
                subtitle: translate('Your new password has been set. You may now sign in.'),
                type: 'success',
            });
        }
        catch (err) {
            if (err instanceof AxiosError && !err.response?.data.errorHandled) {
                addNotification({
                    title: 'Error trying to reset password',
                    subtitle: err.response?.data.message || 'There was an error trying to reset the password. Please try again',
                    type: 'error',
                });
            }
        }
        finally {
            setIsLoading(false);
        }
    };
    const sendMfaChallenge = async (mfaMethodId) => {
        try {
            await iamClient.post(AuthEndpoint.MFA_CHALLENGE, { mfaMethodId });
        }
        catch (err) {
            if (isSessionExpiredError(err)) {
                await handleExpiredSessionDuringMfa();
            }
            throw err;
        }
    };
    const verifyMfaChallenge = async (code) => {
        setIsLoading(true);
        try {
            const { data } = await iamClient.post(AuthEndpoint.MFA_CHALLENGE_VERIFY, {
                code,
                ...(mfaChallenge?.entityId && { entityId: mfaChallenge.entityId }),
            });
            await completeLoginFromResponse(data);
        }
        catch (err) {
            // Only treat 401 as session expired when there is no MFA-specific error code;
            // a 401 with an error code should propagate to the page for inline display.
            if (err instanceof AxiosError && err.response?.status === 401 && !err.response?.data?.code) {
                await handleExpiredSessionDuringMfa();
                return;
            }
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    };
    const enrollMfa = async (type, options) => {
        try {
            const { data } = await iamClient.post(AuthEndpoint.MFA_ENABLE, {
                type,
                ...(options?.mobile && { identifier: options.mobile }),
            });
            return {
                mfaMethodId: (data.mfaMethodId ?? data.mfa_method_id),
                totpUri: (data.totpUri ?? data.totp_uri),
                qrCode: (data.qrCode ?? data.qr_code),
            };
        }
        catch (err) {
            if (isSessionExpiredError(err)) {
                await handleExpiredSessionDuringMfa();
            }
            throw err;
        }
    };
    const verifyMfaEnrollment = async (code, mfaMethodId) => {
        setIsLoading(true);
        try {
            const { data } = await iamClient.post(AuthEndpoint.MFA_ENABLE_VERIFY, {
                code,
                mfaMethodId,
                ...(mfaChallenge?.entityId && { entityId: mfaChallenge.entityId }),
            });
            await completeLoginFromResponse(data);
        }
        catch (err) {
            // Only treat 401 as session expired when there is no MFA-specific error code;
            // a 401 with an error code should propagate to the page for inline display.
            if (err instanceof AxiosError && err.response?.status === 401 && !err.response?.data?.code) {
                await handleExpiredSessionDuringMfa();
                return;
            }
            throw err;
        }
        finally {
            setIsLoading(false);
        }
    };
    const clearMfaChallenge = () => {
        setMfaChallenge(undefined);
    };
    const navigateToLogin = () => {
        if (pathnameRef.current !== AuthRoute.LOGIN) {
            navigate(AuthRoute.LOGIN);
        }
    };
    /**
     * Redirects the user to the default page or to any page that was set in the query params
     */
    const redirectToDefaultPage = async () => {
        const pendingTargetEntityId = pendingTargetEntityIdRef.current;
        if (pendingTargetEntityId && entityIdRef.current !== pendingTargetEntityId) {
            await getEntities(pendingTargetEntityId);
        }
        pendingTargetEntityIdRef.current = undefined;
        const currentSearchParams = searchParamsRef.current;
        const locationParam = currentSearchParams.get('location');
        if (locationParam) {
            const { data: response } = await iamClient.get('/v1/gitbook/doc-url');
            if (response.url) {
                const url = new URL(response.url);
                window.location.href = `${url.origin}${url.pathname}/${locationParam}${url.search}`;
            }
        }
        else {
            const redirectParam = currentSearchParams.get('redirect');
            if (redirectParam && isValidMedableRedirectUrl(redirectParam)) {
                window.location.href = redirectParam;
            }
            else {
                navigate(redirectParam || '/');
            }
        }
    };
    const verifyCustomDomain = (customDomain) => {
        // STUDIO_ENV is inlined at webpack build time. Local-dev bundles skip this redirect because
        // IAM's `momui.host` fallback points at cloud.medable.com (production) — following it would
        // bounce the browser off of localhost whenever the entity has no customDomain configured.
        if (process.env.STUDIO_ENV === 'local' || !customDomain) {
            return;
        }
        const targetOrigin = normalizeCustomDomainOrigin(customDomain);
        if (!targetOrigin || targetOrigin === window.location.origin) {
            return;
        }
        // Strip transient query params that only make sense on the source origin. `expired-session`
        // would trigger an immediate logout on the target; `error_description` surfaces an error
        // notification that's already been handled here.
        const forwardedParams = new URLSearchParams(window.location.search);
        forwardedParams.delete('expired-session');
        forwardedParams.delete('error_description');
        const forwardedSearch = forwardedParams.toString();
        const targetSearch = forwardedSearch ? `?${forwardedSearch}` : '';
        const currentPathname = pathnameRef.current;
        window.location.href =
            currentPathname.startsWith(AuthRoute.LOGIN) || currentPathname.includes(AuthRoute.SWITCH_WORKSPACE)
                ? targetOrigin
                : `${targetOrigin}${currentPathname}${targetSearch}`;
    };
    const providerValue = useMemo(() => ({
        entity,
        entityId,
        forgotPassword,
        getIDPConfiguration,
        isInternalUser,
        isLoading,
        isLoggedIn,
        lastEntities,
        login,
        logout,
        resetExpiredSession,
        mfaChallenge,
        sendMfaChallenge,
        verifyMfaChallenge,
        enrollMfa,
        verifyMfaEnrollment,
        clearMfaChallenge,
        navigateToLogin,
        redirectToDefaultPage,
        refreshIdentity,
        resetPassword,
        refreshEntity,
        scopes,
        setEntity,
        ssoLogin,
        user,
        roleGroups,
        userEntities,
    }), 
    // Only state values — functions are intentionally excluded because they are
    // recreated each render. When useMemo recalculates (state dep changed), the
    // factory picks up fresh closures. When it does NOT recalculate (no state
    // changed), the previous closures are still correct (state hasn't changed).
    // Hook values (searchParams, pathname) are read via refs so they're always current.
    [
        entity,
        entityId,
        isInternalUser,
        isLoading,
        isLoggedIn,
        lastEntities,
        mfaChallenge,
        scopes,
        user,
        roleGroups,
        userEntities,
    ]);
    return (_jsx(AuthContext.Provider, { value: providerValue, children: isInitialized ? (children) : (_jsx("div", { className: 'w-full h-full flex items-center justify-center flex-1', children: _jsx(Spinner, { size: 'xl' }) })) }));
};
